// @ts-nocheck
import React, { Suspense, useEffect } from "react";
import { useRoutes, useLocation, Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { MENU_ITEMS } from "../constants/menu";

import DefaultLayout from "../layouts/Default";
import VerticalLayout from "../layouts/Vertical";
import HorizontalLayout from "../layouts/Horizontal/";

import PrivateRoute from "./PrivateRoute";
import Root from "./Root";
import JwtToken from "./JwtToken";

import { LayoutTypes } from "../constants";

import { useRedux } from "../hooks";

const Login = React.lazy(() => import("../pages/auth/Login"));
const Register = React.lazy(() => import("../pages/auth/Register"));
const Confirm = React.lazy(() => import("../pages/auth/Confirm"));
const ForgetPassword = React.lazy(() => import("../pages/auth/ForgetPassword"));
const LockScreen = React.lazy(() => import("../pages/auth/LockScreen"));
const Logout = React.lazy(() => import("../pages/auth/Logout"));

const RequestAccess = React.lazy(() => import("../components/RequestAccess"));

const DashBoard1 = React.lazy(() => import("../pages/Dashboard/index"));

const Buttons = React.lazy(() => import("../pages/uikit/Buttons"));
const Cards = React.lazy(() => import("../pages/uikit/Cards"));
const Avatars = React.lazy(() => import("../pages/uikit/Avatars"));

const TabsAccordions = React.lazy(
  () => import("../pages/uikit/TabsAccordions"),
);

const Notifications = React.lazy(() => import("../pages/uikit/Notifications"));

const Modals = React.lazy(() => import("../pages/uikit/Modals"));
const Progress = React.lazy(() => import("../pages/uikit/Progress"));
const Offcanvases = React.lazy(() => import("../pages/uikit/Offcanvases"));
const Placeholders = React.lazy(() => import("../pages/uikit/Placeholders"));
const Spinners = React.lazy(() => import("../pages/uikit/Spinners"));
const Images = React.lazy(() => import("../pages/uikit/Images"));
const Carousel = React.lazy(() => import("../pages/uikit/Carousel"));
const EmbedVedio = React.lazy(() => import("../pages/uikit/EmbedVideo"));
const Dropdowns = React.lazy(() => import("../pages/uikit/Dropdowns"));

const PopoversAndTooltips = React.lazy(
  () => import("../pages/uikit/PopoversAndTooltips"),
);

const GeneralUI = React.lazy(() => import("../pages/uikit/GeneralUI"));
const Typography = React.lazy(() => import("../pages/uikit/Typography"));
const Grid = React.lazy(() => import("../pages/uikit/Grid"));

const RangeSliders = React.lazy(() => import("../pages/uikit/RangeSlider"));
const NestableList = React.lazy(() => import("../pages/uikit/NestableList"));
const SweetAlerts = React.lazy(() => import("../pages/uikit/SweetAlerts"));
const Tourpage = React.lazy(() => import("../pages/uikit/TourPage"));
const TreeViewExample = React.lazy(() => import("../pages/uikit/TreeView"));

const FeatherIcons = React.lazy(() => import("../pages/icons/FeatherIcons"));
const MDIIcons = React.lazy(() => import("../pages/icons/MDIIcons"));
const Dripicons = React.lazy(() => import("../pages/icons/DripiIcons"));

const FontAwesomeIcons = React.lazy(
  () => import("../pages/icons/FontAwesomeIcons"),
);

const ThemifyIcons = React.lazy(() => import("../pages/icons/ThemifyIcons"));

const NewTicketInput = React.lazy(() => import("../pages/TicketInput"));
const ContentInput = React.lazy(() => import("../pages/ContentInput"));
const ReadOnlyDetails = React.lazy(() => import("../pages/ReadOnlyDetails"));

const MyTicket = React.lazy(() => import("../pages/MyTicket"));
const HdTickets = React.lazy(() => import("../pages/HdTickets"));
const HdContent = React.lazy(() => import("../pages/HdContent"));
const ProfileDetails = React.lazy(() => import("../pages/Profile"));
const Search = React.lazy(() => import("../pages/Search"));
const TicketDetails = React.lazy(() => import("../pages/TicketDetails"));
const Details = React.lazy(() => import("../pages/Details"));

const MyContent = React.lazy(() => import("../pages/MyContent"));

const MainContact = React.lazy(() => import("../pages/MainContact"));
const ContentCollector = React.lazy(() => import("../pages/ContentCollector"));
const MyFavorites = React.lazy(() => import("../pages/MyFavorites"));
const Maintenance = React.lazy(() => import("../pages/Maintenance"));

const ApexChart = React.lazy(() => import("../pages/chart/ApexChart"));
const ChartJs = React.lazy(() => import("../pages/chart/ChartJs"));

const Landing = React.lazy(() => import("../pages/Landing"));
const Home = React.lazy(() => import("../pages/Home"));
const WhatsNew = React.lazy(() => import("../pages/WhatsNew"));
const NotFoundPage = React.lazy(() => import("../pages/NotFound"));

const Editor = React.lazy(() => import("../pages/Editor"));
const TestEditor = React.lazy(() => import("../pages/TestEditor"));

const loading = () => <div className=""></div>;

type LoadComponentProps = {
  component: React.LazyExoticComponent<() => JSX.Element>;
  mappedTitlePath: string;
};

const findLabelByUrl = (url: string): string | null => {
  for (const item of MENU_ITEMS) {
    const normalize = (str: string) =>
      str.startsWith("/") ? str.substring(1) : str;

    if (normalize(item.url || "") === normalize(url)) {
      return item.label;
    }

    if (item.children) {
      for (const child of item.children) {
        if (normalize(child.url || "") === normalize(url)) {
          return child.label;
        }
      }
    }
  }

  return null;
};

const LoadComponent = ({
  component: Component,
  mappedTitlePath,
}: LoadComponentProps) => {
  const location = useLocation();

  const detailsData = useSelector((state: any) => state.Auth?.detailsData);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);

    const id =
      searchParams.get("ticketId") ||
      searchParams.get("hdTicketId") ||
      searchParams.get("contentId") ||
      searchParams.get("hdContentId");

    const isTicket =
      searchParams.has("ticketId") || searchParams.has("hdTicketId");

    let dynamicTitle = null;

    if (detailsData?.data) {
      if (isTicket && detailsData.data.tk_subject) {
        dynamicTitle = detailsData.data.tk_subject;
      } else if (!isTicket && detailsData.data.cont_title) {
        dynamicTitle = detailsData.data.cont_title;
      }
    }

    if (mappedTitlePath.startsWith("/request-access")) {
      document.title = "Request Access | SWConnect";
    } else if (id && dynamicTitle) {
      document.title = `${id} - ${dynamicTitle} | SWConnect`;
    } else if (id) {
      document.title = `${id} | SWConnect`;
    } else {
      const pageTitle = findLabelByUrl(mappedTitlePath);

      document.title = pageTitle ? `${pageTitle} | SWConnect` : "SWConnect";
    }

    return () => {
      document.title = "SWConnect";
    };
  }, [mappedTitlePath, location.search, detailsData]);

  return (
    <Suspense fallback={loading()}>
      <Component />
    </Suspense>
  );
};

const render = (
  Component: React.LazyExoticComponent<() => JSX.Element>,
  pathToMatch: string,
) => <LoadComponent component={Component} mappedTitlePath={pathToMatch} />;

const RoleTicketRoute = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const userData = localStorage.getItem("userData");

  let parsedUserData: any = {};
  try {
    parsedUserData = userData ? JSON.parse(userData) : {};
  } catch (error) {
    parsedUserData = {};
  }

  const { user_is_hde = false } = parsedUserData;
  const path = location.pathname;
  const searchParams = new URLSearchParams(location.search);

  if (!user_is_hde && path.startsWith("/hd_ticket")) {
    const newPath = path.replace("/hd_ticket", "/ticket/my_ticket");

    if (searchParams.has("hdTicketId")) {
      const ticketId = searchParams.get("hdTicketId");
      searchParams.delete("hdTicketId");
      searchParams.set("ticketId", ticketId);
    }

    return <Navigate to={`${newPath}?${searchParams.toString()}`} replace />;
  }

  if (!user_is_hde && path.startsWith("/ticket") && searchParams.has("hdTicketId")) {
    const ticketId = searchParams.get("hdTicketId");
    searchParams.delete("hdTicketId");
    searchParams.set("ticketId", ticketId);

    return <Navigate to={`${path}?${searchParams.toString()}`} replace />;
  }

  return <>{children}</>;
};

const RoleContentRoute = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const userData = localStorage.getItem("userData");

  let parsedUserData: any = {};
  try {
    parsedUserData = userData ? JSON.parse(userData) : {};
  } catch (error) {
    parsedUserData = {};
  }

  const { user_is_hde = false } = parsedUserData;
  const path = location.pathname;
  const searchParams = new URLSearchParams(location.search);

  if (!user_is_hde && path.startsWith("/hd_content")) {
    const newPath = path.replace("/hd_content", "/content/my_content");

    if (searchParams.has("hdContentId")) {
      const contentId = searchParams.get("hdContentId");
      searchParams.delete("hdContentId");
      searchParams.set("contentId", contentId);
    }

    return <Navigate to={`${newPath}?${searchParams.toString()}`} replace />;
  }

  if (!user_is_hde && path.startsWith("/content") && searchParams.has("hdContentId")) {
    const contentId = searchParams.get("hdContentId");
    searchParams.delete("hdContentId");
    searchParams.set("contentId", contentId);

    return <Navigate to={`${path}?${searchParams.toString()}`} replace />;
  }

  return <>{children}</>;
};

const AllRoutes = () => {
  const { appSelector } = useRedux();

  const { layout } = appSelector((state) => ({
    layout: state.Layout,
  }));

  const getLayout = () => {
    let layoutCls: React.ComponentType = VerticalLayout;

    switch (layout.layoutType) {
      case LayoutTypes.LAYOUT_HORIZONTAL:
        layoutCls = HorizontalLayout;
        break;

      default:
        layoutCls = VerticalLayout;
        break;
    }

    return layoutCls;
  };

  const Layout = getLayout();

  const userData = localStorage.getItem("userData");

  let parsedUserData = {};

  try {
    parsedUserData = userData ? JSON.parse(userData) : {};
  } catch (error) {
    parsedUserData = {};
  }

  const { user_is_hde = false, user_is_admin = false } = parsedUserData as any;

  return useRoutes([
    {
      path: "/",
      element: <Root />,
    },

    {
      path: "/jwt-token",
      element: <JwtToken />,
    },

    {
      path: "/request-access/:object_id/:object_type",
      element: render(RequestAccess, "/request-access/:object_id/:object_type"),
    },

    {
      path: "/",
      element: <DefaultLayout />,
      children: [
        {
          path: "auth",
          children: [
            {
              path: "login",
              element: render(Login, "/auth/login"),
            },
            {
              path: "register",
              element: render(Register, "/auth/register"),
            },
            {
              path: "confirm",
              element: render(Confirm, "/auth/confirm"),
            },
            {
              path: "forget-password",
              element: render(ForgetPassword, "/auth/forget-password"),
            },
            {
              path: "lock-screen",
              element: render(LockScreen, "/auth/lock-screen"),
            },
            {
              path: "logout",
              element: render(Logout, "/auth/logout"),
            },
          ],
        },
      ],
    },

    {
      path: "/",
      element: <PrivateRoute roles={"Admin"} component={Layout} />,

      children: [
        {
          path: "dashboard/default",
          element: render(DashBoard1, "/dashboard/default"),
        },

        {
          path: "base-ui",
          children: [
            {
              path: "buttons",
              element: render(Buttons, "/base-ui/buttons"),
            },
            {
              path: "cards",
              element: render(Cards, "/base-ui/cards"),
            },
            {
              path: "avatars",
              element: render(Avatars, "/base-ui/avatars"),
            },
            {
              path: "tabs-accordions",
              element: render(TabsAccordions, "/base-ui/tabs-accordions"),
            },
            {
              path: "notifications",
              element: render(Notifications, "/base-ui/notifications"),
            },
            {
              path: "modals",
              element: render(Modals, "/base-ui/modals"),
            },
            {
              path: "progress",
              element: render(Progress, "/base-ui/progress"),
            },
            {
              path: "offcanvas",
              element: render(Offcanvases, "/base-ui/offcanvas"),
            },
            {
              path: "placeholders",
              element: render(Placeholders, "/base-ui/placeholders"),
            },
            {
              path: "spinners",
              element: render(Spinners, "/base-ui/spinners"),
            },
            {
              path: "images",
              element: render(Images, "/base-ui/images"),
            },
            {
              path: "carousel",
              element: render(Carousel, "/base-ui/carousel"),
            },
            {
              path: "embedvideo",
              element: render(EmbedVedio, "/base-ui/embedvideo"),
            },
            {
              path: "dropdowns",
              element: render(Dropdowns, "/base-ui/dropdowns"),
            },
            {
              path: "popovers-tooltips",
              element: render(
                PopoversAndTooltips,
                "/base-ui/popovers-tooltips",
              ),
            },
            {
              path: "general",
              element: render(GeneralUI, "/base-ui/general"),
            },
            {
              path: "typography",
              element: render(Typography, "/base-ui/typography"),
            },
            {
              path: "grid",
              element: render(Grid, "/base-ui/grid"),
            },
          ],
        },

        {
          path: "extended-ui",
          children: [
            {
              path: "nestable",
              element: render(NestableList, "/extended-ui/nestable"),
            },
            {
              path: "rangesliders",
              element: render(RangeSliders, "/extended-ui/rangesliders"),
            },
            {
              path: "sweet-alert",
              element: render(SweetAlerts, "/extended-ui/sweet-alert"),
            },
            {
              path: "tour",
              element: render(Tourpage, "/extended-ui/tour"),
            },
            {
              path: "treeview",
              element: render(TreeViewExample, "/extended-ui/treeview"),
            },
          ],
        },

        {
          path: "icons",
          children: [
            {
              path: "feather",
              element: render(FeatherIcons, "/icons/feather"),
            },
            {
              path: "mdi",
              element: render(MDIIcons, "/icons/mdi"),
            },
            {
              path: "dripicons",
              element: render(Dripicons, "/icons/dripicons"),
            },
            {
              path: "font-awesome",
              element: render(FontAwesomeIcons, "/icons/font-awesome"),
            },
            {
              path: "themify",
              element: render(ThemifyIcons, "/icons/themify"),
            },
          ],
        },

        {
          path: "",
          children: [
            {
              path: "profile",
              element: render(ProfileDetails, "/profile"),
            },
            {
              path: "editor",
              element: render(Editor, "/editor"),
            },
            {
              path: "testeditor",
              element: render(TestEditor, "/testeditor"),
            },
          ],
        },

        {
          path: "",
          children: [
            {
              path: "content-input",
              element: render(ContentInput, "/content-input"),
            },
            {
              path: "content/my_content/draft",
              element: render(ContentInput, "/content-input"),
            },
          ],
        },

        {
          path: "",
          children: [
            {
              path: "contact",
              element: render(MainContact, "/contact"),
            },

            {
              path: "content-collector",
              element: render(ContentCollector, "/content-collector"),
            },

            {
              path: "favourites",
              element: render(MyFavorites, "/favourites"),
            },

            user_is_hde && {
              path: "content-collector-maintenance",
              element: render(Maintenance, "/content-collector-maintenance"),
            },
          ].filter(Boolean),
        },

        {
          path: "ticket",
          children: [
            {
              path: "input",
              element: render(NewTicketInput, "/ticket/input"),
            },

            {
              path: "my_ticket",
              element: (
                <RoleTicketRoute>
                  {render(MyTicket, "/ticket/my_ticket")}
                </RoleTicketRoute>
              ),
            },

            {
              path: "my_ticket/details",
              element: (
                <RoleTicketRoute>
                  {render(Details, "/ticket/my_ticket")}
                </RoleTicketRoute>
              ),
            },

            {
              path: "my_ticket/draft",
              element: render(NewTicketInput, "/ticket/input"),
            },
          ],
        },

        {
          path: "",
          children: [
            {
              path: "hd_ticket",
              element: (
                <RoleTicketRoute>
                  {render(HdTickets, "/hd_ticket")}
                </RoleTicketRoute>
              ),
            },

            {
              path: "hd_ticket/:userId",
              element: (
                <RoleTicketRoute>
                  {render(Details, "/hd_ticket")}
                </RoleTicketRoute>
              ),
            },

            {
              path: "draft",
              element: render(NewTicketInput, "/ticket/input"),
            },
          ],
        },

        {
          path: "content",
          children: [
            {
              path: "my_content",
              element: (
                <RoleContentRoute>
                  {render(MyContent, "/content/my_content")}
                </RoleContentRoute>
              ),
            },

            {
              path: "my_content/details",
              element: (
                <RoleContentRoute>
                  {render(Details, "/content/my_content")}
                </RoleContentRoute>
              ),
            },
          ],
        },

        {
          path: "",
          children: [
            {
              path: "hd_content",
              element: (
                <RoleContentRoute>
                  {render(HdContent, "/hd_content")}
                </RoleContentRoute>
              ),
            },

            {
              path: "hd_content/details",
              element: (
                <RoleContentRoute>
                  {render(Details, "/hd_content")}
                </RoleContentRoute>
              ),
            },

            user_is_hde && {
              path: "hd_content/draft",
              element: render(ContentInput, "/content-input"),
            },
          ].filter(Boolean),
        },

        {
          path: "",
          children: [
            {
              path: "Home",
              element: render(Home, "/home"),
            },

            {
              path: "whats-new",
              element: render(WhatsNew, "/whats-new"),
            },
          ],
        },

        {
          path: "",
          children: [
            {
              path: "search",
              element: render(Search, "/search"),
            },

            {
              path: "read-only/details",
              element: render(ReadOnlyDetails, "/read-only/details"),
            },
          ],
        },

        {
          path: "charts",
          children: [
            {
              path: "apex",
              element: render(ApexChart, "/charts/apex"),
            },

            {
              path: "chartjs",
              element: render(ChartJs, "/charts/chartjs"),
            },
          ],
        },
      ],
    },

    {
      path: "*",
      element: render(NotFoundPage, "*"),
    },
  ]);
};

export { AllRoutes };